import {
  Component,
  OnInit,
  Input,
  Injectable,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { FileUploader, FileLikeObject, FileItem } from 'ng2-file-upload';
import { Subscription } from 'rxjs';

import { Person } from 'src/app/people/models/person';
import { AWSService } from 'src/app/shared/services/aws.service';
import { DocumentScanEvent } from '../../models/documentScanEvent';
import { DocumentScanStatusType } from '../../models/documentScanStatusType';
import { buildFileUploader, oneMb } from './document-upload.helper';

const maxFileSizeInMb = 10;
const allowedFileTypes = ['image', 'doc', 'xls', 'ppt', 'pdf'];

@Component({
  selector: 'document-upload',
  templateUrl: 'document-upload.component.html',
  styleUrls: ['document-upload.component.scss'],
})
@Injectable()
export class DocumentUploadComponent implements OnInit {
  virusScanSubscription: Subscription;
  @Input()
  person: Person;
  uploader: FileUploader;
  errorMessage: string;

  displayDocumentScanningMessage: boolean = false;

  @Input()
  onFileUpload: (file: FileItem) => void;
  @Input()
  doUpload: boolean = true;
  @Input()
  doScan: boolean = true;
  @Input()
  displayUploadButton: boolean = true;
  @Input()
  uploadText: string = 'UPLOAD DOCUMENT';
  @Input()
  uploadButtonClass: string = 'mat-primary';
  @Output() virusScanResultEvent = new EventEmitter<DocumentScanEvent>();
  @ViewChild('documentUpload', { static: true })
  documentUpload: ElementRef;

  constructor(private awsService: AWSService) {
    this.virusScanSubscription = this.awsService.virusScanResult.subscribe((result) => {
      const comp = this;
      this.virusScanResultEvent.emit({
        status: result.status,
        documentPassedScan: result.documentPassedScan,
        errorMessage: result.errorMessage,
      });
      comp.displayDocumentScanningMessage = false;
    });
  }

  ngOnInit(): void {
    this.setupUploader();
  }

  showUpload() {
    this.documentUpload.nativeElement.click();
  }

  ngOnDestroy() {
    this.virusScanSubscription.unsubscribe();
  }

  private setupUploader() {
    const comp = this;

    this.uploader = buildFileUploader({
      allowedFileType: allowedFileTypes,
      maxFileSizeInMb: maxFileSizeInMb,
    });

    this.uploader.onAfterAddingFile = (file) => {
      comp.errorMessage = null;
      if (comp.onFileUpload) {
        comp.onFileUpload(file);
      }
      if (comp.doUpload) {
        this.displayDocumentScanningMessage = true;
        comp.awsService.uploadToS3(file, comp.person).subscribe(() => {
          const comp = this;
          comp.displayDocumentScanningMessage = false;
        });
      }
      if (comp.doScan) {
        this.displayDocumentScanningMessage = true;
        comp.awsService.scanForVirus(file);
        this.virusScanResultEvent.emit({
          status: DocumentScanStatusType.Started,
          documentPassedScan: false,
        });
      }
    };

    this.uploader.onWhenAddingFileFailed = (item, filter, options) =>
      comp.onWhenAddingFileFailed(item, filter, options);
  }

  private onWhenAddingFileFailed(item: FileLikeObject, filter: any, options: any) {
    switch (filter.name) {
      case 'fileSize':
        let fileSize = Math.round((item.size / oneMb) * 100) / 100;
        let errorMessage = `Maximum upload size exceeded (${fileSize} MB of ${maxFileSizeInMb} MB allowed)`;
        this.awsService.openSnackBar(errorMessage, false);
        break;
      case 'fileType':
        this.awsService.openSnackBar('The selected file type is not supported', false);
        break;
      default:
        console.error(`Unknown error (filter is ${filter.name})`);
        this.awsService.openSnackBar(
          'The selected file cannot be uploaded, please try again with a different file.',
          false
        );
    }
  }
}
